import { useCallback } from 'react';
import { Form, useFormikContext } from 'formik';

import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';

import type { EMPTY_STRING } from '@e-socna/empty';
import type { AccountingWorkflowId } from '@e-socna/data-layer';

import { useReferencesContext } from '../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { useUiContext } from '../../contexts/UiContext/hooks/useUiContext.hook';
import { useMissionReadyContext } from '../../contexts/MissionContext/hooks/useMissionReadyContext.hook';
import { filterWorkflowsOfMissionWorkflow } from '../../utils/workflows-of-mission-workflow.util';
import { selectableWorkflowsOf } from '../../utils/selectable-workflows.util';
import { IdSelect } from '../../components/IdSelect';
import type { IdOption } from '../../components/IdSelect';
import type { AccountingInfoFormValues } from '../../pages/MissionAccounting/hooks/useInitHandler.hook';

const CONTAINER_STYLE = {
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
} as const;

interface Props {
  readonly submitLabel: string;
}

export const MissionAccountingInfoFormFields = ({
  submitLabel,
}: Readonly<Props>) => {
  const { ui } = useUiContext();
  const { accountingWorkflows } = useReferencesContext();
  const { mission } = useMissionReadyContext();

  const {
    handleSubmit: formikSubmit,
    isSubmitting,
    isValid,
    setFieldValue,
    values,
  } = useFormikContext<AccountingInfoFormValues>();

  const isLoading = isSubmitting || ui.isBackgroundOperationRunning;

  const handleWorkflowIdChange = useCallback(
    (id: AccountingWorkflowId | typeof EMPTY_STRING) => {
      void setFieldValue('workflowId', id);
    },
    [setFieldValue],
  );

  return (
    <Form
      onSubmit={formikSubmit}
      // eslint-disable-next-line react/forbid-component-props
      style={CONTAINER_STYLE}
    >
      <Grid container spacing={2} sx={CONTAINER_STYLE}>
        <Grid size={12} sx={{ pt: 1, pb: 1 }}>
          <IdSelect<AccountingWorkflowId, IdOption<AccountingWorkflowId>>
            id="mission-accounting-info-workflow-id"
            label="Workflow"
            name="workflowId"
            onChange={handleWorkflowIdChange}
            options={filterWorkflowsOfMissionWorkflow(
              selectableWorkflowsOf(accountingWorkflows),
              mission.workflowId,
            ).map((workflow) => ({
              id: workflow._id,
              label: workflow.label,
            }))}
            required
            value={values.workflowId as AccountingWorkflowId}
          />
        </Grid>
        <Grid size={12} sx={{ marginTop: 'auto', pt: 1, pb: 1 }}>
          <Stack direction="row" spacing={2}>
            <Button
              disabled={!isValid}
              loading={isLoading}
              size="large"
              type="submit"
              variant="contained"
            >
              {submitLabel}
            </Button>
          </Stack>
        </Grid>
      </Grid>
    </Form>
  );
};
